import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideBook, lucideDraftingCompass, lucideFileText, lucidePencilRuler } from '@ng-icons/lucide';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { distinctUntilChanged } from 'rxjs';
import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { RéférentielMétierRessourcesProfessionnellesService } from '../../référentiel-métier-ressources-professionnelles.service';
import { RessourceTypeName } from '../../services/ressource.service';
import { ProductionStore } from '../../store/production.store';
import { RessourceStore } from '../../store/ressource.store';
import { RessourceFormItemComponent } from './ressource-form-item/ressource-form-item.component';

@Component({
  selector: 'app-ressources-professionnelles-form',
  imports: [ButtonAddNewComponent, ReactiveFormsModule, RessourceFormItemComponent],
  templateUrl: './ressources-professionnelles-form.component.html',
  providers: [provideIcons({ lucideBook, lucideDraftingCompass, lucidePencilRuler, lucideFileText })],
})
export class RessourcesProfessionnellesFormComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #ressourceStore = inject(RessourceStore);
  readonly #productionStore = inject(ProductionStore);
  readonly #ressourceProfessionnelleService = inject(RéférentielMétierRessourcesProfessionnellesService);

  readonly cartographie = input.required<Cartographie>();
  readonly idProduction = input<string>();
  readonly type = input<string>();

  readonly productions = linkedSignal(() => this.#productionStore.entities());

  readonly form = this.#fb.nonNullable.group({
    ressources: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
    this.ressources.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged()).subscribe(() => {
      this.ressources.controls.forEach((control) => {
        control.get('nom')?.setErrors(null);
      });
    });
  }

  constructor() {
    effect(() => {
      this.#ressourceStore.chargerToutes(this.cartographie().id);
      this.addRessource();
    });
  }

  addRessource() {
    const type = this.type();
    const ressource = this.#fb.nonNullable.group({
      ressource: [null, [Validators.required]],
      type: [type ?? null, [Validators.required]],
      idsProductions: [this.idProduction() ? [this.idProduction()] : [], [Validators.required]],
    });
    this.ressources.push(ressource);
  }

  onSubmit() {
    const toastrId = uuidv7();
    const ressources = (
      this.ressources.value as {
        ressource: { nom: string };
        type: RessourceTypeName;
        idsProductions: string[];
      }[]
    )
      .filter((x) => !!x.ressource?.nom.trim() && x.type)
      .map((x) => ({ nom: x.ressource.nom, type: x.type, idsProductions: x.idsProductions }));
    if (!ressources || ressources.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#ressourceStore.créer({
      idCartographie: this.cartographie().id,
      data: {
        ressources,
      },
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des ressources professionnelles réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#ressourceProfessionnelleService.refreshData.next(true);
        this.#sidepanelService.close();
      },
    });
  }

  get ressources() {
    return this.form.get('ressources') as FormArray<FormGroup>;
  }
}
